import { defineStore } from 'pinia'

import { ADMIN_ROLES } from '@/constants/permissions'
import logger from '@/services/logger'
import Auth from '@/services/models/Auth'
import DigitalAccounts from '@/services/models/DigitalAccounts.js'
import Features from '@/services/models/Features.js'

const LAST_LINK_KEY = 'income:lastLink'
const STORE_KEY = 'authStore'
// Vínculo e perspectiva vivem por aba: fechar a aba desfaz o vínculo do admin
// (decisão de produto, task 86akqpnf2). F5 preserva, pois o sessionStorage sobrevive.
const LINK_KEYS = ['client_id', 'perspective']
// O /me e o que deriva do vínculo também ficam na aba: outra aba que troque de conta
// regrava o localStorage, e o F5 desta aba precisa voltar para a conta dela.
// O localStorage continua com esses dados como "última conta usada" (base da aba nova).
const TAB_SCOPED_KEYS = [
  ...LINK_KEYS,
  'auth_data',
  'featureList',
  'digitalAccountsList',
  'selectedAccount',
]

// Resolvido sob demanda: specs em ambiente node importam a store sem sessionStorage.
const tabStorage = {
  getItem: key => sessionStorage.getItem(key),
  setItem: (key, value) => sessionStorage.setItem(key, value),
}

const initialState = () => ({
  status: '',
  client_id: '',
  perspective: 'client',
  auth_data: {},
  tokenData: {},
  featureList: [],
  digitalAccountsList: [],
  selectedAccount: {},
})

const readSharedState = () => {
  try {
    return JSON.parse(localStorage.getItem(STORE_KEY) || '{}') || {}
  } catch {
    return {}
  }
}

// Foto do localStorage antes da hidratação da aba: o $patch da aba dispara o
// subscriber do localStorage e o sobrescreve com a cópia da aba. Se a cópia for
// descartada (outro usuário), o restoreSession volta para esta foto.
let sharedStateBeforeTabHydration = null

// A hidratação da aba vem depois da do localStorage e usa $patch (merge profundo):
// sem limpar antes, chaves do /me de outra conta que faltem no da aba sobreviveriam.
// Só limpa o que a aba tem: cópias antigas (só client_id/perspective) mantêm o resto.
const resetTabScopedState = ({ store }) => {
  sharedStateBeforeTabHydration = readSharedState()
  let tabCopy = {}
  try {
    tabCopy = JSON.parse(sessionStorage.getItem(STORE_KEY) || '{}') || {}
  } catch {
    return
  }
  const initial = initialState()
  TAB_SCOPED_KEYS.filter(key => key in tabCopy).forEach(key => {
    store[key] = initial[key]
  })
}

// Dados do /me que a aba copia; LINK_KEYS ficam de fora porque o vínculo é recomposto.
const ME_KEYS = TAB_SCOPED_KEYS.filter(key => !LINK_KEYS.includes(key))

// Usuário dono do token atual (claim `user_id` do SimpleJWT, igual ao `auth_data.user.id`).
// Lido do localStorage: o login de outra aba troca o token de todas.
const readTokenUserId = () => {
  try {
    const token = localStorage.getItem('access')
    if (!token) return null
    return JSON.parse(atob(token.split('.')[1])).user_id ?? null
  } catch {
    return null
  }
}

// Sessões anteriores a esta mudança guardavam o client_id no localStorage.
// Lido uma única vez no boot para não derrubar o usuário comum já logado.
const readLegacyClientId = () => {
  try {
    return JSON.parse(localStorage.getItem(STORE_KEY) || '{}').client_id || ''
  } catch {
    return ''
  }
}

export const useAuthStore = defineStore('authStore', {
  id: 'auth',
  state: initialState,
  actions: {
    login(data) {
      this.status = 'success'
      this.tokenData = JSON.parse(atob(data.access.split('.')[1]))
      localStorage.setItem('access', data.access)
      localStorage.setItem('refresh', data.refresh)
    },
    async me(data) {
      this.auth_data = data
      this.featureList = data.link_features?.enabled_features || []
      if (data.link) {
        this.digitalAccounts()
      }
    },
    async refreshMe() {
      try {
        // localLoader: hidratação de estado em background, sem overlay global.
        const resp = await Auth.me({ localLoader: true })
        await this.me(resp.data)
        return resp.data
      } catch (error) {
        throw error
      }
    },
    setLink(link) {
      return new Promise(resolve => {
        this.client_id = link
        this.perspective = 'client'
        this.rememberLastLink(link)
        resolve(true)
      })
    },
    setPerspective(perspective) {
      this.perspective = perspective === 'admin' ? 'admin' : 'client'
    },
    // Chamado uma vez no boot (src/services/session.js), depois da hidratação e dos
    // interceptors. Síncrona: nenhuma navegação espera pela rede.
    restoreSession() {
      if (!this.isLoggedIn) return
      if (!this.isTabCopyValid()) this.discardTabCopy()
      if (!this.client_id) {
        if (this.auth_data?.user?.is_superuser) {
          // Aba nova: admin volta desvinculado. O auth_data NÃO é tocado: ele veio do
          // localStorage compartilhado e pode ser o /me de outra aba ainda vinculada.
          this.perspective = 'client'
          return
        }
        const restored = this.getLastLink() || readLegacyClientId()
        if (!restored) return
        this.setLink(restored)
      }
      // F5 hidrata a cópia da aba (consistente). Aba nova herda o /me da última conta
      // usada, que pode não ser a restaurada: reconcilia em background.
      if (String(this.auth_data?.link?.id ?? '') === String(this.client_id)) return
      this.refreshMe().catch(error => {
        logger.warn('[authStore] falha ao reconciliar o /me com o vínculo da aba', error)
      })
    },
    // A cópia da aba só vale para o usuário do token atual: logout/login em outra aba
    // deixa aqui um /me vazio ou de outro usuário. Sem token não há sessão a preservar
    // (logout em outra aba apagou o `access`): a cópia é descartada.
    isTabCopyValid() {
      const user = this.auth_data?.user
      if (!user) return false
      const tokenUserId = readTokenUserId()
      return tokenUserId !== null && String(user.id) === String(tokenUserId)
    },
    // Volta para o /me compartilhado (localStorage) e desfaz o vínculo da aba.
    // Atribuição direta (não $patch) para não mesclar com a cópia descartada.
    discardTabCopy() {
      const shared = sharedStateBeforeTabHydration ?? readSharedState()
      if (!shared.auth_data?.user) return
      const initial = initialState()
      ME_KEYS.forEach(key => {
        this[key] = key in shared ? shared[key] : initial[key]
      })
      this.client_id = ''
      this.perspective = 'client'
    },
    rememberLastLink(link) {
      const userId = this.auth_data?.user?.id
      if (!userId || !link) return

      try {
        localStorage.setItem(`${LAST_LINK_KEY}:${userId}`, String(link))
      } catch (error) {
        logger.warn('[authStore] falha ao gravar a última conta no localStorage', error)
      }
    },
    getLastLink() {
      const userId = this.auth_data?.user?.id
      if (!userId) return null

      try {
        return localStorage.getItem(`${LAST_LINK_KEY}:${userId}`) || null
      } catch (error) {
        logger.warn('[authStore] falha ao ler a última conta do localStorage', error)
        return null
      }
    },
    removeLink() {
      return new Promise(resolve => {
        this.client_id = ''
        this.perspective = 'client'
        this.auth_data.link = {}
        resolve(true)
      })
    },
    logout() {
      localStorage.removeItem('access')
      localStorage.removeItem('refresh')
      this.$reset()
    },
    async features(isAdmin) {
      this.featureList = await Features.getFeaturesByClient(this.auth_data?.link?.id, isAdmin)
    },
    digitalAccounts() {
      // localLoader: lista usada pelo seletor de contas (background), sem overlay global.
      DigitalAccounts.list('?custom_page_size=-1', { localLoader: true }).then(resp => {
        this.digitalAccountsList = resp.data.results
        this.selectedAccount = this.auth_data?.link?.digital_account || null
      })
    },
    update_picture(picture) {
      this.auth_data.link.picture = picture
    },
  },
  getters: {
    isLoggedIn: state => state.status === 'success',
    isLinkedIn: state => state.client_id !== '',
    isCompany: state => state.auth_data.link.resourcetype === 'Company',
    authStatus: state => state.status,
    authData: state => state.auth_data,
    mfaData: state => state.auth_data?.mfa_data ?? null,
    userId: state => state.auth_data?.user?.id,
    linkId: state => state.client_id,
    link: state => state.auth_data.link,
    isValidated: state => !!state.auth_data.link?.validated,
    isSuperuser: state => state.auth_data?.user?.is_superuser || false,
    // "Modo administrativo": admin logado que ainda não escolheu um cliente.
    // Ponto único da regra — a sidebar, o SidebarEntry, o header e o guard de
    // rota derivavam isso cada um por conta própria (review PR #1948).
    isUnlinkedAdmin: state =>
      (state.auth_data?.user?.is_superuser || false) && state.client_id === '',
    // Admin superuser com um cliente vinculado (qualquer perspectiva).
    isLinkedAdmin: state =>
      (state.auth_data?.user?.is_superuser || false) && state.client_id !== '',
    // Decide o LAYOUT administrativo (menu, tema da sidebar, header). Sem vínculo o
    // admin só tem a perspectiva admin; vinculado, depende do toggle/rota.
    // O filtro de DADOS não passa por aqui: vem do header Client (interceptors.js).
    isAdminPerspective: state =>
      (state.auth_data?.user?.is_superuser || false) &&
      (state.client_id === '' || state.perspective === 'admin'),
    isAdmin: state =>
      state.auth_data?.user?.is_superuser || state.auth_data?.user?.is_staff || false,
    isAdminMaster: state => state.auth_data?.user?.admin_role === ADMIN_ROLES.ADMIN_MASTER || false,
    getUserFeatures: state => state.featureList,
    getDigitalAccountsList: state =>
      state.digitalAccountsList.filter(element => element.id != state.selectedAccount.id),
    getSelectedAccount: state => state.selectedAccount,
    getOwnContactId: state =>
      state.auth_data.user.is_superuser ? '' : state.auth_data.link.own_contact,
    getOwnContactInfo: state => state.auth_data.link_own_contact,
    // TODO fix pulling isStaff from back-end
    isStaff: () => false,
    adminPermissions: state => state.auth_data?.user?.admin_permissions || [],
    adminRole: state => state.auth_data?.user?.admin_role || [],
    hasPermission: state => permission => {
      if (state.auth_data?.user?.admin_role === ADMIN_ROLES.ADMIN_MASTER) {
        return true
      }

      const permissions = state.auth_data?.user?.admin_permissions || []

      if (Array.isArray(permission)) {
        return permission.some(p => permissions.includes(p))
      }

      return permissions.includes(permission)
    },
    hasAllPermissions: state => permissionList => {
      if (state.auth_data?.user?.admin_role === ADMIN_ROLES.ADMIN_MASTER) {
        return true
      }

      const permissions = state.auth_data?.user?.admin_permissions || []
      return permissionList.every(p => permissions.includes(p))
    },
    userPermissions: state => state.auth_data?.user?.user_permissions || [],
    hasUserPermission: state => permission => {
      const permissions = state.auth_data?.user?.user_permissions || []
      if (Array.isArray(permission)) {
        return permission.some(p => permissions.includes(p))
      }
      return permissions.includes(permission)
    },
  },
  persist: [
    // A ordem importa: o plugin hidrata na ordem do array, então a cópia da aba prevalece.
    { key: STORE_KEY, omit: LINK_KEYS },
    {
      key: STORE_KEY,
      pick: TAB_SCOPED_KEYS,
      storage: tabStorage,
      beforeHydrate: resetTabScopedState,
    },
  ],
})
